import React, { useState } from 'react';
import { StyledPaper, StyledTable, StyledTableRow, StyledTableCell } from '../../../uiComponents/MuiTable/StyledTable.js'
import MuiTableHeader from '../../../uiComponents/MuiTable/MuiTableHeader.js';
import { stableSort, getComparator } from '../../../uiComponents/MuiTable/MuiHelper.js';
import { 
  TableBody,
  TableContainer,
  TextField,
  MenuItem
} from '@mui/material';
import { DateUtils } from '../../../utils/DateUtils.js';
import muiInputStyles from '../../../uiComponents/MuiInput/MuiInputStyles.js';

const headCells = [ // setting these to noSort until we are getting response with a variant id to use instead of table index
  { label: 'Chromosome', id: 'chrom', numeric: false, disablePadding: false },
  { label: 'Position', id: 'pos', numeric: false, disablePadding: false },
  { label: 'Ref', id: 'ref', numeric: false, disablePadding: false},
  { label: 'Alt', id: 'alt', numeric: false, disablePadding: false },
  { label: 'Current Review Status', id: 'hasBeenReviewed', numeric: false, disablePadding: false },
  { label: 'Last Reviewed Date', id: 'lastReviewed', numeric: false, disablePadding: false },
  { label: 'Operation', id: 'defaultAction', numeric: false, disablePadding: false,  type: 'noSort' }, 
];

export default function ConfirmMolecularWorkflowTable(props) {
  const classes = muiInputStyles();
  const [tableData] = useState(props.data);
  const [order, setOrder] = useState('desc'); //default sort direction
  const [orderBy, setOrderBy] = useState('chromosome'); //default sort column for table

  const handleRequestSort = (event, property) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
  };  

  const operationCell = (row) => {
    // Convert row.defaultAction to boolean for the select input because we will save boolean in the form
    const actionMapping = {
      "Create new review": true,
      "Do not create review": false
    };
    
    const selectedValue = row.hasBeenReviewed 
      ? props.selectedValues[row.chrom + '-' + row.pos] ?? actionMapping[row.defaultAction] // Default from selectedValues if reviewed, else fallback to defaultAction
      : actionMapping["Create new review"]; // Always set "Create new review" if not reviewed
      
    const disabled = !row.hasBeenReviewed;
  
    // Prevent clicks if "disabled". If we use the disabled prop on the 
    // textfield, screen readers will not be able to read the value if the
    // input is disabled.
    // Note that cursor: not-allowed needs to be applied both on the MUI table
    // cell itself and the div wrapping the MUI TextField due to the way MUI
    // works with cursor events.
    return (
      <div
        style={{
          pointerEvents: disabled ? "none" : "auto", 
          cursor: disabled ? "not-allowed" : "auto",
        }}
      >
        <TextField
          fullWidth
          size="small"
          id={`selectOperation-${row.chrom}-${row.pos}`}
          className={classes.muiInputs}
          value={selectedValue}
          onChange={(event) => props.handleTableSelectionChange(row.chrom, row.pos, event.target.value)}
          variant="outlined"
          label="Select Operation"
          select
          role="combobox"
          aria-required="true"
          aria-readonly={disabled} // Keep it readable for screen readers
          aria-controls={`selectOperation-${row.chrom}-${row.pos}`}
          aria-expanded="false"
          tabIndex={disabled ? 0 : null}
          SelectProps={{ 
            MenuProps: { 
              disableScrollLock: true, 
              disablePortal: true, // makes the popover render inside the normal React tree (instead of attaching it to the body)
            }, 
            open: disabled ? false : undefined, // Prevents the menu from opening when disabled
          }}
          InputProps={{
            style: {
              backgroundColor: disabled ? "#e9ecef" : "#fff", // background MUI wrapper color for disabled
            },
          }}
        >
          <MenuItem value={true}>Create new review</MenuItem>
          <MenuItem value={false}>Do not create review</MenuItem>
        </TextField>
      </div>
    );
  };

  const lastReviewedCell = (row) => {
    return row.hasBeenReviewed && row.lastReviewed 
      ? DateUtils.FormatLocalDate(row.lastReviewed)
      : '';
  };

  const reviewStatusCell = (hasBeenReviewed) => hasBeenReviewed ? "Reviewed" : "Never Reviewed";

  return (
    <>
      <StyledPaper>
        <TableContainer>
          <StyledTable
            size='small'
            aria-label="Molecular Review Settings" 
          >
            <MuiTableHeader 
              headCells={headCells} 
              order={order}  
              orderBy={orderBy} 
              onRequestSort={handleRequestSort}
            />
            <TableBody>
            { tableData.length ? (
              stableSort(tableData, getComparator(order, orderBy))
                .map((row, index) => {
                return (
                  <StyledTableRow tabIndex={-1} key={index}>
                    <StyledTableCell>{row.chrom}</StyledTableCell>
                    <StyledTableCell>{row.pos}</StyledTableCell>
                    <StyledTableCell>{row.ref}</StyledTableCell>
                    <StyledTableCell>{row.alt}</StyledTableCell>
                    <StyledTableCell>{reviewStatusCell(row.hasBeenReviewed)}</StyledTableCell>
                    <StyledTableCell>{lastReviewedCell(row)}</StyledTableCell>
                    <StyledTableCell style={{
                      cursor: row.hasBeenReviewed ? "auto" : "not-allowed",  // Adjust cursor style based on `hasBeenReviewed`
                      width: '25%',
                    }}>
                      {operationCell(row)}
                    </StyledTableCell>
                  </StyledTableRow>
                );
                })
              ) : (
                <StyledTableRow>
                  <StyledTableCell style={{textAlign: 'center'}} colSpan={12}>No file contents found</StyledTableCell>
                </StyledTableRow>
              )
            }
            </TableBody>
          </StyledTable>
        </TableContainer>
      </StyledPaper>
    </>
  );
}
